import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Show, useRecordContext } from 'react-admin'
import { Box } from '@mui/material'
import { client } from '../../../providers/data/client'
import ResultHeaderBar from './ResultHeaderBar'
import ResultHero from './ResultHero'
import ClinicalDetailCard from './ClinicalDetailCard'
import MessagingCard from './MessagingCard'
import SampleImageGrid from './SampleImageGrid'
import PatientCard from './PatientCard'
import FollowUpStatusCard from './FollowUpStatusCard'

// Read-only kit result page. This component owns the fetch (via react-admin's
// Show) and the prev/next list context; every child below is presentational
// and takes data as props.
const ResultDetailContent = () => {
  const record = useRecordContext()
  const location = useLocation()
  const navigate = useNavigate()

  const [sampleImages, setSampleImages] = useState(record?.sampleImages || [])
  const [sampleImagesError, setSampleImagesError] = useState(Boolean(record?.sampleImagesError))

  // Scroll to top only when the kit itself changes (prev/next nav), not on
  // every record refetch — record?.sampleImages gets a new array reference
  // on every refetch (e.g. after Acknowledge calls refresh()), which would
  // otherwise yank the page back to the top after an unrelated click.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [record?.kitId])

  // Sync local image state whenever the record's images change — this still
  // covers kit navigation and any refetch that brings freshly-signed URLs.
  useEffect(() => {
    setSampleImages(record?.sampleImages || [])
    setSampleImagesError(Boolean(record?.sampleImagesError))
  }, [record?.sampleImages, record?.sampleImagesError])

  const refetchSampleImages = useCallback(async () => {
    if (!record?.kitId) return
    try {
      const response = await client().get(`provider-portal/results/${record.kitId}`)
      setSampleImages(response.data?.sampleImages || [])
      setSampleImagesError(Boolean(response.data?.sampleImagesError))
    } catch (err) {
      console.error('Failed to refresh sample image URLs', err)
      setSampleImagesError(true)
    }
  }, [record?.kitId])

  // Prev/next only navigate within the ordered kit list the provider actually
  // came from (passed as route state by ResultList's KitId link) — if the
  // page was opened directly, there's no order to infer, so nav is hidden.
  const orderedIds = location.state?.resultListIds
  const hasContext = Array.isArray(orderedIds) && orderedIds.length > 0
  const currentIndex = hasContext ? orderedIds.indexOf(record?.kitId) : -1
  const hasPrev = hasContext && currentIndex > 0
  const hasNext = hasContext && currentIndex >= 0 && currentIndex < orderedIds.length - 1

  const goTo = (kitId) => navigate(`/provider-portal/results/${kitId}/show`, { state: { resultListIds: orderedIds } })

  const nav = {
    hasContext,
    hasPrev,
    hasNext,
    onPrev: () => hasPrev && goTo(orderedIds[currentIndex - 1]),
    onNext: () => hasNext && goTo(orderedIds[currentIndex + 1]),
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <ResultHeaderBar record={record} nav={nav} />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: 3,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <ResultHero record={record} />
          {!['pendingConfirmation', 'manualReview'].includes(record?.status) && <ClinicalDetailCard record={record} />}
          {!['pendingConfirmation', 'manualReview'].includes(record?.status) && <MessagingCard record={record} />}
          {!['pendingConfirmation', 'manualReview'].includes(record?.status) && (
            <SampleImageGrid images={sampleImages} loadError={sampleImagesError} onExpired={refetchSampleImages} />
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <PatientCard record={record} />
          <FollowUpStatusCard record={record} />
        </Box>
      </Box>
    </Box>
  )
}

export default function ResultDetail({ id }) {
  const kitRecord = useRecordContext()

  return (
    <Show title={kitRecord?.kitId ?? 'Kit Details'} emptyWhileLoading resource="provider-portal/results" id={id}>
      <ResultDetailContent />
    </Show>
  )
}
